import React, {PureComponent} from "react";
import BarChart, {TooltipItem} from "components/charts/BarChart";
import {loadLandlordBookingsRevenue} from "services/bookings/landlords";
import {DateRange} from "util/time";
import {handleError} from "util/errors";
import {LandlordRevenuePeriod} from "services/entities/booking";
import ProgressSpinner from "components/ProgressSpinner";
import {Currency, CurrencyAmount} from "util/currency";
import './index.scss';

interface RevenueChartProps {
  dateRange: DateRange|null;
}

interface RevenueChartState {
  revenuePeriods: LandlordRevenuePeriod[]|null;
  currency: Currency|null;
}

export default class RevenueChart extends PureComponent<RevenueChartProps, RevenueChartState> {
  state: RevenueChartState = {
    revenuePeriods: null,
    currency: null
  };

  componentDidMount() {
    if(this.props.dateRange) {
      this.loadRevenue(this.props.dateRange);
    }
  }

  componentDidUpdate(prevProps: Readonly<RevenueChartProps>, prevState: Readonly<RevenueChartState>, snapshot?: any) {
    const range = this.props.dateRange;
    if(prevProps.dateRange !== range) {
      if(range) {
        this.loadRevenue(range);
      } else {
        this.setState({revenuePeriods: null});
      }
    }
  }

  render() {
    const {revenuePeriods, currency} = this.state;
    if(revenuePeriods && currency) {
      return this.renderChart(revenuePeriods, currency);
    } else {
      return this.renderLoading();
    }
  }

  renderChart(revenuePeriods: LandlordRevenuePeriod[], currency: Currency) {
    const data = [];
    const labels = [];
    let index = 0;
    for(const p of revenuePeriods) {
      labels.push(`Week ${index + 1}`);
      data.push(p.revenue.amount);
      index++;
    }
    return <div className="revenue-chart">
      <div className="y-axis-label">{ currency.symbol }</div>
      <div className="x-axis-label">Weekly revenue</div>
      <BarChart data={[data]} tooltip={{renderTitle: this.renderTooltipTitle, renderLabel: this.renderTooltipLabel}}
                xAxis={{labels: labels}} yAxis={{renderLabel: (val, i) => this.renderYAxisLabel(val, i, currency)}} />
    </div>;
  }

  renderYAxisLabel = (value: number, index: number, currency: Currency) => {
    if(Math.round(value) !== value) {
      // ignore non-whole numbers
      return null;
    }
    const amount = new CurrencyAmount(value, currency);
    return amount.format("0k");
  };

  renderTooltipTitle = (items:TooltipItem[]) => {
    const weekOffset = items[0].dataIndex;
    const rangeStart = this.props.dateRange!.start;
    const startDate = weekOffset > 0 ? rangeStart.addUnits(weekOffset, 'weeks') : rangeStart;
    const endDate = startDate.addUnits(6, 'days');
    return `${startDate.format('MMM DD, YYYY')} - ${endDate.format('MMM DD, YYYY')}`;
  };

  renderTooltipLabel = (item: TooltipItem) => {
    const value = item.raw;
    const amount = new CurrencyAmount(value, this.state.currency!);
    return amount.format("$0k");
  };

  renderLoading() {
    return <div className="revenue-chart loading">
      <ProgressSpinner />
    </div>;
  }

  loadRevenue(dateRange: DateRange) {
    loadLandlordBookingsRevenue(dateRange.start, dateRange.end).then(
      revenue => {
        this.setState({revenuePeriods: revenue.periods, currency: revenue.currency});
      },
      ex => {
        handleError('Failed to load revenue data', ex);
      }
    );
  }
}